import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Plus, AlertCircle, CheckCircle, Clock, Ticket as TicketIcon, Pin, PinOff, Timer, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import CreateBoardModal from '../components/CreateBoardModal';


function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateBoard, setShowCreateBoard] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    loadData();
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    try {
      const response = await api.get(`/employees/check-admin`);
      setIsAdmin(response.data.is_admin);
    } catch (error) {
      console.error('Failed to check admin status:', error);
    }
  };

  const loadData = async () => {
    try {
      const [statsRes, boardsRes] = await Promise.all([
        api.get(`/dashboard/stats`),
        api.get(`/boards`),
      ]);
      setStats(statsRes.data);
      setBoards(boardsRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleBoardCreated = (newBoard) => {
    setBoards([...boards, newBoard]);
    setShowCreateBoard(false);
  };

  const handleDeleteBoard = async (e, boardId, boardName) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!window.confirm(`Are you sure you want to delete "${boardName}"?\n\nThis will permanently delete the board and ALL tickets in it. This action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await api.delete(`/boards/${boardId}`);
      setBoards(boards.filter(b => b.board_id !== boardId));
      toast.success(response.data.message);
    } catch (error) {
      console.error('Failed to delete board:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete board');
    }
  };

  const handleTogglePin = async (e, boardId) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const response = await api.post(`/boards/${boardId}/pin`, {});
      setBoards(boards.map(b => 
        b.board_id === boardId ? { ...b, is_pinned: response.data.is_pinned } : b
      ).sort((a, b) => {
        if (a.is_pinned && !b.is_pinned) return -1;
        if (!a.is_pinned && b.is_pinned) return 1;
        return 0;
      }));
      toast.success(response.data.is_pinned ? 'Board pinned' : 'Board unpinned');
    } catch (error) {
      console.error('Failed to toggle pin:', error);
      toast.error('Failed to update pin status');
    }
  };

  const handleStatClick = (filterType) => {
    navigate(`/tickets?filter=${filterType}`);
  };

  const formatTime = (minutes) => {
    if (!minutes) return '--';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours < 24) return `${hours}h ${mins}m`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto p-6" data-testid="dashboard">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-stone-900 mb-2">
          Dashboard
        </h1>
        <p className="text-sm text-stone-600 leading-relaxed">
          Overview of your tickets and activity
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <button
          onClick={() => handleStatClick('my_total')}
          data-testid="stat-my-total"
          className="bg-white border border-stone-200 rounded-xl p-6 shadow-soft hover:shadow-gold hover:border-primary/30 transition-all text-left"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">
              My Total Tickets
            </p>
            <TicketIcon className="w-5 h-5 text-stone-400" strokeWidth={1.5} />
          </div>
          <p className="text-3xl font-bold text-stone-900">{stats?.my_total || 0}</p>
        </button>

        <button
          onClick={() => handleStatClick('my_pending')}
          data-testid="stat-my-pending"
          className="bg-white border border-stone-200 rounded-xl p-6 shadow-soft hover:shadow-gold hover:border-primary/30 transition-all text-left"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">
              My Pending
            </p>
            <Clock className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
          </div>
          <p className="text-3xl font-bold text-stone-900">{stats?.my_pending || 0}</p>
        </button>

        <button
          onClick={() => handleStatClick('my_high_priority')}
          data-testid="stat-my-high-priority"
          className="bg-white border border-stone-200 rounded-xl p-6 shadow-soft hover:shadow-gold hover:border-primary/30 transition-all text-left"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">
              High Priority
            </p>
            <AlertCircle className="w-5 h-5 text-rose-500" strokeWidth={1.5} />
          </div>
          <p className="text-3xl font-bold text-stone-900">{stats?.my_high_priority || 0}</p>
        </button>

        <button
          onClick={() => handleStatClick('my_completed')}
          data-testid="stat-my-completed"
          className="bg-white border border-stone-200 rounded-xl p-6 shadow-soft hover:shadow-gold hover:border-primary/30 transition-all text-left"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">
              My Completed
            </p>
            <CheckCircle className="w-5 h-5 text-emerald-500" strokeWidth={1.5} />
          </div>
          <p className="text-3xl font-bold text-stone-900">{stats?.my_completed || 0}</p>
        </button>

        <div className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-xl p-6 shadow-soft">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-primary-800 uppercase tracking-wider">
              Avg. Time To Complete
            </p>
            <Timer className="w-5 h-5 text-primary" strokeWidth={1.5} />
          </div>
          <p className="text-3xl font-bold text-stone-900">{formatTime(stats?.avg_time_to_complete)}</p>
        </div>
      </div>

      {/* My Boards Section (only if user has pinned boards) */}
      {boards.some(b => b.is_pinned) && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold tracking-tight text-stone-900">My Boards</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {boards.filter(b => b.is_pinned).map((board) => (
              <Link
                key={board.board_id}
                to={`/board/${board.board_id}`}
                data-testid={`board-card-${board.board_id}`}
                className="bg-white border border-primary/30 ring-1 ring-primary/10 rounded-xl p-6 shadow-soft hover:shadow-gold transition-all group relative"
              >
                <div className="absolute top-4 right-4 flex items-center gap-1">
                  {isAdmin && (
                    <button
                      onClick={(e) => handleDeleteBoard(e, board.board_id, board.name)}
                      data-testid={`delete-board-${board.board_id}`}
                      className="p-2 rounded-lg text-stone-400 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                      title="Delete board"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  )}
                  <button
                    onClick={(e) => handleTogglePin(e, board.board_id)}
                    data-testid={`pin-board-${board.board_id}`}
                    className="p-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                  >
                    <Pin className="w-4 h-4" strokeWidth={2} />
                  </button>
                </div>
                <h3 className="text-xl font-medium text-stone-800 mb-2 group-hover:text-stone-900 transition-colors pr-20">
                  {board.name}
                </h3>
                <p className="text-xs text-stone-500 uppercase tracking-wider">
                  Created {new Date(board.created_at).toLocaleDateString()}
                </p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* All Boards Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold tracking-tight text-stone-900">All Boards</h2>
          <Button
            data-testid="create-board-button"
            onClick={() => setShowCreateBoard(true)}
            className="bg-primary hover:bg-primary-hover text-stone-900 rounded-xl shadow-gold font-semibold transition-all hover:-translate-y-0.5 active:translate-y-0"
          >
            <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
            New Board
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {boards.filter(b => !b.is_pinned).map((board) => (
            <Link
              key={board.board_id}
              to={`/board/${board.board_id}`}
              data-testid={`board-card-${board.board_id}`}
              className={`bg-white border rounded-xl p-6 shadow-soft hover:shadow-gold transition-all group relative ${
                board.is_pinned ? 'border-primary/30' : 'border-stone-200 hover:border-primary/30'
              }`}
            >
              <div className="absolute top-4 right-4 flex items-center gap-1">
                {isAdmin && (
                  <button
                    onClick={(e) => handleDeleteBoard(e, board.board_id, board.name)}
                    data-testid={`delete-board-${board.board_id}`}
                    className="p-2 rounded-lg text-stone-400 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                    title="Delete board"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                )}
                <button
                  onClick={(e) => handleTogglePin(e, board.board_id)}
                  data-testid={`pin-board-${board.board_id}`}
                  className={`p-2 rounded-lg transition-colors ${
                    board.is_pinned 
                      ? 'bg-primary/10 text-primary hover:bg-primary/20' 
                      : 'text-stone-400 hover:bg-stone-100 hover:text-stone-600'
                  }`}
                >
                  {board.is_pinned ? (
                    <Pin className="w-4 h-4" strokeWidth={2} />
                  ) : (
                    <PinOff className="w-4 h-4" strokeWidth={1.5} />
                  )}
                </button>
              </div>
              <h3 className="text-xl font-medium text-stone-800 mb-2 group-hover:text-stone-900 transition-colors pr-20">
                {board.name}
              </h3>
              <p className="text-xs text-stone-500 uppercase tracking-wider">
                Created {new Date(board.created_at).toLocaleDateString()}
              </p>
            </Link>
          ))}

          {boards.filter(b => !b.is_pinned).length === 0 && boards.length === 0 && (
            <div className="col-span-full text-center py-12 text-stone-500">
              <p>No boards yet. Create your first board to get started.</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-stone-900 mb-4">
          My Recent Activity
        </h2>
        <div className="bg-white border border-stone-200 rounded-xl shadow-soft">
          {stats?.recent_tickets && stats.recent_tickets.length > 0 ? (
            <div className="divide-y divide-stone-200">
              {stats.recent_tickets.map((ticket) => (
                <div key={ticket.ticket_id} className="p-4 hover:bg-stone-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-stone-800">{ticket.title}</h4>
                      <p className="text-sm text-stone-500 mt-1">
                        Updated {new Date(ticket.updated_at).toLocaleString()}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium border ${
                        ticket.priority === 'high'
                          ? 'bg-rose-50 text-rose-700 border-rose-200'
                          : ticket.priority === 'medium'
                          ? 'bg-blue-50 text-blue-700 border-blue-200'
                          : 'bg-stone-100 text-stone-600 border-stone-200'
                      }`}
                    >
                      {ticket.priority}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-stone-500">
              <p>No recent activity</p>
            </div>
          )}
        </div>
      </div>

      {showCreateBoard && (
        <CreateBoardModal
          onClose={() => setShowCreateBoard(false)}
          onBoardCreated={handleBoardCreated}
        />
      )}
    </div>
  );
}

export default Dashboard;
